import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { AppNotification, NotificationsService } from '../../../services/notifications/notifications.service';

/**
 * Campana de notificaciones del header, entre el indicador de dólar observado y el menú de usuario.
 *
 * Es la bandeja de toda la aplicación: no conoce ningún módulo. Qué se muestra y cómo se redacta lo
 * decide {@link NotificationsService} a través de su registro de traductores por tipo de evento.
 */
@Component({
  selector: 'app-notifications-menu',
  templateUrl: './notifications-menu.component.html',
  styleUrl: './notifications-menu.component.scss',
  standalone: true,
  imports: [CommonModule, ButtonModule, BadgeModule, PopoverModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsMenuComponent implements OnInit {
  private readonly notificationsService = inject(NotificationsService);
  private readonly router = inject(Router);

  readonly notifications = this.notificationsService.notifications;
  readonly unreadCount = this.notificationsService.unreadCount;

  ngOnInit(): void {
    this.notificationsService.start();
  }

  get badgeValue(): string {
    const count = this.unreadCount();
    return count > 9 ? '9+' : String(count);
  }

  get tooltip(): string {
    const count = this.unreadCount();
    if (!count) return 'Notificaciones';
    return count === 1 ? '1 notificación sin leer' : `${count} notificaciones sin leer`;
  }

  /** Abrir la bandeja equivale a haberlas visto: no hay un "leído" por notificación. */
  onOpen(): void {
    this.notificationsService.markAllAsRead();
  }

  clear(): void {
    this.notificationsService.clear();
  }

  openNotification(notification: AppNotification): void {
    if (!notification.link) return;
    this.router.navigate(notification.link.path, { queryParams: notification.link.queryParams });
  }

  relativeTime(timestamp: string): string {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const elapsedMinutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (elapsedMinutes < 1) return 'Hace instantes';
    if (elapsedMinutes < 60) return `Hace ${elapsedMinutes} min`;

    const elapsedHours = Math.round(elapsedMinutes / 60);
    if (elapsedHours < 24) return `Hace ${elapsedHours} h`;

    return new Intl.DateTimeFormat('es-CL', { dateStyle: 'short', timeStyle: 'short' }).format(date);
  }

  trackById(_index: number, notification: AppNotification): string {
    return notification.id;
  }
}
